import { Link } from "@tanstack/react-router";
import { BrandLogo } from "@/components/brand-logo";
import { Mail, Phone, MapPin, Facebook } from "lucide-react";

const FACEBOOK_URL = "https://www.facebook.com/61590505047040/?locale=vi_VN";

const PRODUCT_LINKS = [
  { label: "Tính năng", href: "#features" },
  { label: "Các vai trò", href: "#portals" },
  { label: "Bảng giá", href: "#pricing" },
  { label: "Bản demo", href: "/login" },
];

const SUPPORT_LINKS: Array<{ label: string; href: string; external?: boolean }> = [
  { label: "Hướng dẫn sử dụng", href: "/guide" },
  { label: "Chính sách bảo mật", href: "/privacy" },
  { label: "Điều khoản dịch vụ", href: "/terms" },
  { label: "Liên hệ qua Facebook", href: FACEBOOK_URL, external: true },
];

export function LandingFooter() {
  return (
    <footer id="contact" className="border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950/60">
      <div className="max-w-7xl mx-auto px-5 md:px-8 py-14 md:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-8">
          {/* Brand */}
          <div className="sm:col-span-2 lg:col-span-1">
            <Link to="/" className="flex items-center gap-2.5">
              <BrandLogo size="size-10" />
              <div className="leading-tight">
                <div className="font-display font-semibold text-slate-900 dark:text-white text-[17px] tracking-tight">
                  Ezyclass
                </div>
                <div className="text-[11px] text-slate-500 dark:text-slate-400 -mt-0.5">
                  Quản lý trung tâm học tập
                </div>
              </div>
            </Link>
            <p className="mt-4 text-[14px] leading-[1.6] text-slate-500 dark:text-slate-400 max-w-xs">
              Phần mềm quản lý trung tâm giáo dục — đơn giản, rõ ràng, dễ sử dụng cho quản lý, giáo viên và học viên.
            </p>
            <a
              href={FACEBOOK_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-4 inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-[#1877F2] text-white text-[13px] font-medium hover:bg-[#0e63cf] transition-colors"
            >
              <Facebook className="size-4" />
              Theo dõi Facebook
            </a>
          </div>

          {/* Product */}
          <div>
            <h4 className="text-[13px] font-semibold text-slate-900 dark:text-white uppercase tracking-wider">
              Sản phẩm
            </h4>
            <ul className="mt-4 space-y-2.5">
              {PRODUCT_LINKS.map((l) => (
                <li key={l.label}>
                  <a
                    href={l.href}
                    className="text-[14px] text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:text-white transition-colors"
                  >
                    {l.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Support */}
          <div>
            <h4 className="text-[13px] font-semibold text-slate-900 dark:text-white uppercase tracking-wider">
              Hỗ trợ
            </h4>
            <ul className="mt-4 space-y-2.5">
              {SUPPORT_LINKS.map((l) => (
                <li key={l.label}>
                  {l.external ? (
                    <a
                      href={l.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[14px] text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:text-white transition-colors inline-flex items-center gap-1.5"
                    >
                      {l.label}
                    </a>
                  ) : l.href.startsWith("/") ? (
                    <Link
                      to={l.href}
                      className="text-[14px] text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:text-white transition-colors"
                    >
                      {l.label}
                    </Link>
                  ) : (
                    <a
                      href={l.href}
                      className="text-[14px] text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:text-white transition-colors"
                    >
                      {l.label}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-[13px] font-semibold text-slate-900 dark:text-white uppercase tracking-wider">
              Liên hệ
            </h4>
            <ul className="mt-4 space-y-3">
              <li className="flex items-center gap-2.5 text-[14px] text-slate-600 dark:text-slate-400">
                <Mail className="size-4 text-slate-400 dark:text-slate-500 shrink-0" />
                <a href="mailto:infoezyclass@gmail.com" className="hover:text-slate-900 dark:text-white">
                  infoezyclass@gmail.com
                </a>
              </li>
              <li className="flex items-center gap-2.5 text-[14px] text-slate-600 dark:text-slate-400">
                <Phone className="size-4 text-slate-400 dark:text-slate-500 shrink-0" />
                <a href="tel:0965108401" className="hover:text-slate-900 dark:text-white">
                  0965 108 401
                </a>
              </li>
              <li className="flex items-start gap-2.5 text-[14px] text-slate-600 dark:text-slate-400">
                <MapPin className="size-4 text-slate-400 dark:text-slate-500 shrink-0 mt-0.5" />
                <span>66 Nguyễn Hoàng, Cầu Giấy, Hà Nội</span>
              </li>
              <li className="flex items-center gap-2.5 text-[14px] text-slate-600 dark:text-slate-400">
                <Facebook className="size-4 text-slate-400 dark:text-slate-500 shrink-0" />
                <a
                  href={FACEBOOK_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-slate-900 dark:text-white"
                >
                  facebook.com/ezyclass
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-12 pt-6 border-t border-slate-200 dark:border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-3">
          <p className="text-[13px] text-slate-600 dark:text-slate-400">
            © {new Date().getFullYear()} Ezyclass. Tất cả quyền được bảo lưu.
          </p>
          <div className="flex items-center gap-5">
            <Link to="/privacy" className="text-[13px] text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors">
              Bảo mật
            </Link>
            <Link to="/terms" className="text-[13px] text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors">
              Điều khoản
            </Link>
            <Link to="/guide" className="text-[13px] text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-colors">
              Hướng dẫn
            </Link>
          </div>

        </div>
      </div>
    </footer>
  );
}
